import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';

const ServiciosPage = () => {
  useEffect(() => {
    document.title = 'Servicios de Pladur en Barcelona | Tabiques, Techos, Aislamientos';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Servicios profesionales de pladur en Barcelona: tabiques, trasdosados, falsos techos, aislamiento acústico y térmico. Presupuesto personalizado según m2 y complejidad.');
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://barcelona.pladures.es';

    // Add WebPage Schema
    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Servicios de Pladur en Barcelona",
      "description": "Servicios profesionales de pladur en Barcelona: tabiques, trasdosados, falsos techos, aislamiento acústico y térmico.",
      "url": `${siteUrl}/servicios`,
      "inLanguage": "es-ES",
      "isPartOf": {
        "@type": "WebSite",
        "name": "PLADUREX Barcelona",
        "url": siteUrl
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Inicio",
            "item": `${siteUrl}/`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Servicios",
            "item": `${siteUrl}/servicios`
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(webPageSchema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      icon: 'ri-layout-grid-line',
      title: 'Tabiques de pladur',
      description: 'Divisiones interiores ligeras y resistentes para redistribuir espacios en viviendas, oficinas y locales comerciales.',
      includes: [
        'Tabiques simples y dobles',
        'Opciones con aislamiento acústico',
        'Paso de instalaciones eléctricas y fontanería',
        'Refuerzos para colgar elementos pesados',
      ],
      useCases: 'Ideal para crear nuevas habitaciones, separar ambientes, dividir oficinas o crear zonas de almacenamiento.',
      priceFactors: 'El precio depende de los m², altura, tipo de placa (estándar, hidrófuga, ignífuga), nivel de aislamiento y acabado.',
      image: 'https://public.readdy.ai/ai/img_res/2a5400aff18c427718e6b04ddadd24de.jpg',
    },
    {
      icon: 'ri-building-line',
      title: 'Trasdosados',
      description: 'Revestimiento de paredes existentes para mejorar aislamiento, ocultar instalaciones o corregir irregularidades.',
      includes: [
        'Trasdosado directo sobre pared',
        'Trasdosado autoportante con estructura',
        'Aislamiento térmico y acústico',
        'Soluciones antihumedad',
      ],
      useCases: 'Perfecto para mejorar el confort térmico, reducir ruido de vecinos, ocultar tuberías o cables, y renovar paredes antiguas.',
      priceFactors: 'Varía según m², tipo de trasdosado (directo o autoportante), grosor de aislamiento, tipo de placa y estado de la pared base.',
      image: 'https://readdy.ai/api/search-image?query=wall%20cladding%20installation%20with%20drywall%20and%20insulation%20material%20interior%20construction&width=800&height=600&seq=service-detail-02&orientation=landscape',
    },
    {
      icon: 'ri-stack-line',
      title: 'Falsos techos',
      description: 'Techos suspendidos continuos o registrables para ocultar instalaciones, mejorar acústica y crear diseños modernos.',
      includes: [
        'Falsos techos continuos lisos',
        'Techos registrables desmontables',
        'Integración de iluminación LED',
        'Aislamiento acústico superior',
      ],
      useCases: 'Excelente para bajar altura de techos, ocultar vigas y cables, mejorar aislamiento térmico y crear efectos de iluminación indirecta.',
      priceFactors: 'El coste depende de m², altura del techo, tipo (continuo o registrable), complejidad del diseño, acceso y acabado.',
      image: 'https://readdy.ai/api/search-image?query=suspended%20false%20ceiling%20with%20white%20plasterboard%20finish%20recessed%20lighting%20modern%20interior&width=800&height=600&seq=service-detail-03&orientation=landscape',
    },
    {
      icon: 'ri-sound-module-line',
      title: 'Aislamiento acústico',
      description: 'Soluciones especializadas para reducir la transmisión de ruido entre estancias o desde el exterior.',
      includes: [
        'Lana mineral de alta densidad',
        'Placas de pladur acústicas',
        'Sistemas de doble placa',
        'Bandas resilientes antivibratorias',
      ],
      useCases: 'Fundamental para dormitorios, salas de música, oficinas con reuniones, locales comerciales y viviendas con ruido exterior.',
      priceFactors: 'Precio según m², nivel de aislamiento requerido (dB), grosor del sistema, tipo de lana mineral y complejidad de la instalación.',
      image: 'https://readdy.ai/api/search-image?query=acoustic%20insulation%20with%20mineral%20wool%20between%20drywall%20panels%20soundproofing%20construction&width=800&height=600&seq=service-detail-04&orientation=landscape',
    },
    {
      icon: 'ri-temp-cold-line',
      title: 'Aislamiento térmico',
      description: 'Mejora la eficiencia energética de tu espacio reduciendo pérdidas de calor en invierno y entrada de calor en verano.',
      includes: [
        'Lana mineral o poliestireno',
        'Trasdosados con aislamiento',
        'Falsos techos con cámara aislante',
        'Soluciones para fachadas interiores',
      ],
      useCases: 'Ideal para reducir facturas de calefacción y aire acondicionado, mejorar confort térmico y cumplir normativas de eficiencia.',
      priceFactors: 'Varía según m², grosor del aislante, tipo de material, ubicación (paredes o techos) y complejidad del proyecto.',
      image: 'https://readdy.ai/api/search-image?query=thermal%20insulation%20material%20between%20drywall%20panels%20energy%20efficiency%20construction&width=800&height=600&seq=service-detail-05&orientation=landscape',
    },
    {
      icon: 'ri-drop-line',
      title: 'Pladur antihumedad',
      description: 'Placas especiales resistentes a la humedad para baños, cocinas y zonas con alta exposición al agua.',
      includes: [
        'Placas hidrófugas (verdes)',
        'Tratamiento de juntas especial',
        'Preparación para alicatado',
        'Ventilación adecuada',
      ],
      useCases: 'Imprescindible en baños, cocinas, lavaderos, sótanos y cualquier espacio con riesgo de condensación o salpicaduras.',
      priceFactors: 'El precio depende de m², tipo de placa hidrófuga, preparación de la superficie, tratamiento de juntas y acabado final.',
      image: 'https://readdy.ai/api/search-image?query=green%20moisture%20resistant%20drywall%20installation%20in%20bathroom%20water%20resistant%20construction&width=800&height=600&seq=service-detail-06&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 bg-gradient-to-br from-primary-dark to-primary overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=construction%20tools%20and%20materials%20for%20drywall%20installation%20organized%20workspace&width=1920&height=800&seq=servicios-hero-001&orientation=landscape"
              alt="Servicios de pladur"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-white font-extrabold text-3xl sm:text-5xl md:text-6xl mb-6"
            >
              Servicios profesionales de pladur en Barcelona
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-white/90 text-xl leading-relaxed max-w-3xl mx-auto"
            >
              Soluciones completas para viviendas, oficinas y locales comerciales. Presupuesto personalizado según tus necesidades.
            </motion.p>
          </div>
        </section>

        {/* Services Detail */}
        <section className="py-24 bg-bg-light">
          <div className="max-w-7xl mx-auto px-6">
            <div className="space-y-20">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                      <i className={`${service.icon} text-primary text-4xl`}></i>
                    </div>
                    <h2 className="text-primary-dark text-4xl font-bold mb-4">
                      {service.title}
                    </h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-6">
                      {service.description}
                    </p>

                    <div className="mb-6">
                      <h3 className="text-primary-dark text-xl font-bold mb-3">
                        ¿Qué incluye?
                      </h3>
                      <ul className="space-y-2">
                        {service.includes.map((item, i) => (
                          <li key={i} className="flex items-start space-x-3">
                            <i className="ri-check-line text-primary text-xl mt-0.5 flex-shrink-0"></i>
                            <span className="text-gray-600 text-[15px]">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-primary-dark text-xl font-bold mb-3">
                        Casos de uso
                      </h3>
                      <p className="text-gray-600 text-[15px] leading-relaxed">
                        {service.useCases}
                      </p>
                    </div>

                    <div className="bg-blue-50 border-l-4 border-primary p-6 rounded-r-lg mb-6">
                      <h3 className="text-primary-dark text-lg font-bold mb-2">
                        Factores que afectan al precio
                      </h3>
                      <p className="text-gray-600 text-[15px] leading-relaxed">
                        {service.priceFactors}
                      </p>
                    </div>

                    <a
                      href="tel:+34629501941"
                      className="inline-flex items-center space-x-2 bg-primary text-white px-6 py-3 rounded-lg font-bold hover:bg-primary-dark transition-all duration-300 whitespace-nowrap"
                    >
                      <i className="ri-phone-line text-xl"></i>
                      <span>Solicitar presupuesto</span>
                    </a>
                  </div>

                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-[500px] object-cover object-top rounded-2xl shadow-2xl"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Zonas de Servicio */}
        <section className="py-24 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-primary-dark text-4xl md:text-5xl font-serif font-bold mb-6">
                Zonas de servicio en Barcelona
              </h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Realizamos <strong>instalación de pladur en Barcelona</strong> y área metropolitana. Desde nuestra base en Sant Andreu, llegamos a toda la ciudad.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                'Sant Andreu',
                'Eixample',
                'Sants',
                'Les Corts',
                'Gràcia',
                'Sant Martí',
                'Ciutat Vella',
                'Horta-Guinardó',
                'Nou Barris',
                'Sant Gervasi',
                'Sarrià',
                'Poblenou',
              ].map((zone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-bg-light rounded-lg p-4 text-center hover:bg-primary hover:text-white transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-map-pin-line text-2xl mb-2"></i>
                  <p className="font-semibold text-sm">{zone}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 bg-primary">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-white text-4xl md:text-5xl font-bold mb-6"
            >
              ¿Necesitas un presupuesto personalizado?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-white/90 text-lg mb-8"
            >
              Cuéntanos tu proyecto y te ofreceremos un presupuesto detallado sin compromiso.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4"
            >
              <a
                href="tel:+34629501941"
                className="bg-white text-primary px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                <span>+34 629 501 941</span>
              </a>
              <Link
                to="/contacto"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-primary transition-all duration-300 whitespace-nowrap"
              >
                Formulario de contacto
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

export default ServiciosPage;
